import _ from 'lodash';
import { BaseApiService } from './base-api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AdjustFacilityAction,
  Facility,
  FacilityActionResult,
} from '../domain/interfaces/facility.interface';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Occupation, OccupationPerHour } from '../domain/interfaces/occupation.interface';
import { TerminalSummaryPerTerminalType } from '../domain/interfaces/terminal.interface';
import {
  FacilitySummary,
  FacilitySummaryGrouped,
  NotificationCount,
} from '../domain/interfaces/facility-summary.interface';
import { ConfigService } from 'src/app/@shared/services/config.service';
import { ParkbaseStorageService } from 'src/app/@shared/services/parkbaseStorage.service';
import {
  FilteredTerminals,
  TerminalResponse,
} from '../domain/interfaces/terminal-response.interface';

@Injectable({
  providedIn: 'root',
})
export class FacilitiesApiService extends BaseApiService {
  private _facilities = new BehaviorSubject<Facility[]>([]);
  readonly facilities = this._facilities.asObservable();
  private _terminals = new BehaviorSubject<FilteredTerminals>({
    entry: { count: 0, label: 'Inritten' },
    exit: { count: 0, label: 'Uitritten' },
    barrier: { count: 0, label: 'Slagbomen' },
  });
  readonly terminals = this._terminals.asObservable();
  private _facilitySummaryGrouped = new BehaviorSubject<FacilitySummaryGrouped>({});
  readonly facilitySummaryGrouped = this._facilitySummaryGrouped.asObservable();
  private _facilityTerminalsSummary = new BehaviorSubject<TerminalSummaryPerTerminalType[]>([]);
  readonly facilityTerminalsSummary = this._facilityTerminalsSummary.asObservable();
  private _occupationPerHourCurrent = new BehaviorSubject<Occupation>({
    occupationRatios: [],
    reservationOccupationRatios: [],
  });
  readonly occupationPerHourCurrent = this._occupationPerHourCurrent.asObservable();

  private _dataStore: {
    facilities: Facility[];
    terminals: any;
    facilitySummaryGrouped: FacilitySummaryGrouped;
    facilityTerminalsSummary: TerminalSummaryPerTerminalType[];
    occupationPerHourCurrent: Occupation;
  } = {
    facilities: [],
    terminals: {},
    facilitySummaryGrouped: {},
    facilityTerminalsSummary: [],
    occupationPerHourCurrent: { occupationRatios: [], reservationOccupationRatios: [] },
  };

  constructor(
    configService: ConfigService,
    httpClient: HttpClient,
    parkbaseStorageService: ParkbaseStorageService
  ) {
    super(configService, httpClient);
    super.setStorage(parkbaseStorageService);
    this.loadFacilities();
  }

  /**
   * GET /api/gui/v1.0/facilities
   */
  loadFacilities(): void {
    this.http
      .get<Facility[]>(this.parkbaseBl9BaseUrl + '/facilities', {
        headers: this.setHeaders(),
      })
      .subscribe(
        (data) => {
          this._dataStore.facilities = data;
          this._facilities.next(Object.assign({}, this._dataStore).facilities);
          this.parkbaseStorageService.storeFacilities(data);
        },
        (error) => this.handleError('Could not load facilities.', error)
      );
  }

  /**
   * POST /api/gui/v1.0/facilities/{id}/action
   */
  sendFacilityAction(
    id: number,
    facilityAction: AdjustFacilityAction
  ): Observable<FacilityActionResult> {
    if (!id) {
      throw new Error('id not provided');
    }
    const apiUrl = this.parkbaseBl9BaseUrl + '/facilities/' + id + '/action';
    this.logger.info('-- sendFacilityAction');
    return this.http.post<FacilityActionResult>(apiUrl, facilityAction, {
      headers: this.setHeaders(),
    });
  }

  /**
   * GET /api/gui/v1.0/facilities/{id}/terminals/summary
   */
  loadFacilityTerminalsSummary(id: number): void {
    if (!id) {
      throw new Error('id not provided');
    }
    this.http
      .get<TerminalSummaryPerTerminalType[]>(
        this.parkbaseBl9BaseUrl + '/facilities/' + id + '/terminals/summary',
        {
          headers: this.setHeaders(),
        }
      )
      .subscribe({
        next: (data) => {
          setTimeout(() => {
            this._dataStore.facilityTerminalsSummary = data;
            this._facilityTerminalsSummary.next(
              Object.assign({}, this._dataStore).facilityTerminalsSummary
            );
          }, environment.apiTimeout);
        },
        error: (error) => {
          if (error.status === 404) {
            this._dataStore.facilityTerminalsSummary = [];
            this._facilityTerminalsSummary.next(
              Object.assign({}, this._dataStore).facilityTerminalsSummary
            );
          } else {
            this.handleError('Could not load facilities terminals summary.', error);
          }
        },
      });
  }

  /**
   * GET /api/gui/v1.0/facilities/{id}/occupation-ratio/current
   */
  loadTotals(id: number): void {
    if (!id) {
      throw new Error('id not provided');
    }
    this.http
      .get<Occupation>(
        this.parkbaseBl9BaseUrl + '/facilities/' + id + '/occupation-ratio/current',
        { headers: this.setHeaders() }
      )
      .subscribe(
        (data) => {
          // Is an array now, would an object not be nicer here?
          setTimeout(() => {
            this._dataStore.occupationPerHourCurrent = data;
            this._occupationPerHourCurrent.next(
              Object.assign({}, this._dataStore).occupationPerHourCurrent
            );
          }, environment.apiTimeout);
        },
        (error) => this.handleError('Could not load occupationRatio.', error)
      );
  }

  /**
   * POST /api/gui/v1.0/facilities/{id}/occupation-ratio/history
   */
  loadActivity(id: number, utcDate: any): Observable<OccupationPerHour[]> {
    if (!id) {
      throw new Error('id not provided');
    }
    const body = {
      utcDate,
    };
    return this.http
      .post<OccupationPerHour[]>(
        this.parkbaseBl9BaseUrl + '/facilities/' + id + '/occupation-ratio/history',
        body,
        { headers: this.setHeaders() }
      )
      .pipe(retry(2), catchError(this.getError));
  }

  /**
   * GET /api/gui/v1.0/divisions/summary
   */
  loadFacilitySummaryGrouped(): void {
    let timeout = 0;
    let selectedFacilities = this.parkbaseStorageService.retrieveFacilitiesChoice();
    if (selectedFacilities.length === 0) {
      const choiceOptions: any[] = [];
      this.http
        .get<Facility[]>(this.parkbaseBl9BaseUrl + '/facilities', {
          headers: this.setHeaders(),
        })
        .subscribe((facilities) => {
          facilities.forEach((f) => {
            choiceOptions.push({
              selected: true,
              facility: f,
            });
          });
          this.parkbaseStorageService.storeFacilitiesChoice(choiceOptions);
          selectedFacilities = choiceOptions;
        });

      timeout = 100;
    }
    this.http
      .get<FacilitySummary[]>(this.parkbaseBl9BaseUrl + '/divisions/summary', {
        headers: this.setHeaders(),
      })
      .subscribe(
        (data) => {
          const facilitySummaryGrouped: FacilitySummaryGrouped = {
            facilitiesWithAlarm: [],
            facilitiesWithWarning: [],
            facilitiesStatusOk: [],
          };
          const lowPriorityFacilities: any[] = [];
          data.forEach((f) => {
            selectedFacilities.forEach((sf) => {
              if (sf.selected && f.facility.id === sf.facility.id) {
                const facilityWithAlarm = _.find(f.emergencyAlarms, { going: true });
                if (facilityWithAlarm) {
                  facilitySummaryGrouped.facilitiesWithAlarm!.push(f);
                } else {
                  let totalForAlarmNotifications = 0;
                  let totalForErrorAndWarnings = 0;
                  let totalForInfo = 0;
                  f.notificationCounts.forEach((n: NotificationCount) => {
                    if (n.level === 6) {
                      totalForAlarmNotifications += n.count;
                    }
                    if (n.level === 1 || n.level === 2) {
                      totalForErrorAndWarnings += n.count;
                    }
                    if (n.level === 0) {
                      totalForInfo += n.count;
                    }
                  });
                  // give some priority to alarm, error and warnings
                  if (
                    totalForErrorAndWarnings > totalForInfo / 2 ||
                    totalForAlarmNotifications > 0
                  ) {
                    facilitySummaryGrouped.facilitiesWithWarning!.push(f);
                  } else {
                    lowPriorityFacilities.push(f);
                  }
                }
              }
            });
          });
          facilitySummaryGrouped.facilitiesWithAlarm = _.uniq(
            facilitySummaryGrouped.facilitiesWithAlarm
          );
          facilitySummaryGrouped.facilitiesWithWarning = _.uniq(
            facilitySummaryGrouped.facilitiesWithWarning
          );
          let statusOk = _.uniq(lowPriorityFacilities);
          statusOk = _.orderBy(
            statusOk,
            [(statusOk: any) => statusOk.facility.name.toLowerCase()],
            ['asc']
          );
          facilitySummaryGrouped.facilitiesStatusOk = statusOk;

          setTimeout(() => {
            this._dataStore.facilitySummaryGrouped = facilitySummaryGrouped;
            this._facilitySummaryGrouped.next(
              Object.assign({}, this._dataStore).facilitySummaryGrouped
            );
          }, timeout);
        },
        (error) => this.handleError('Could not load divisons summary.', error)
      );
  }

  loadTerminals(facility: Facility): void {
    const body = {
      query: {
        page: 1,
        pageSize: 25,
      },
    };
    const filteredTerminals: FilteredTerminals = {
      entry: { count: 0, label: 'Inritten' },
      exit: { count: 0, label: 'Uitritten' },
      barrier: { count: 0, label: 'Slagbomen' },
    };
    this.http
      .post<TerminalResponse>(
        this.parkbaseBl9BaseUrl + '/facilities/' + facility.id + '/terminals',
        body,
        {
          headers: this.setHeaders(),
        }
      )
      .subscribe((result) => {
        result?.terminals?.forEach((t) => {
          switch (t.type.id) {
            case 1:
              filteredTerminals.entry.count += 1;
              break;
            case 2:
              filteredTerminals.exit.count += 1;
              break;
            case 3:
              filteredTerminals.barrier.count += 1;
              break;
            case 11:
              filteredTerminals.entry.count += 1;
              filteredTerminals.exit.count += 1;
              break;
          }
        });
        this._dataStore.terminals = filteredTerminals;
        this._terminals.next(Object.assign({}, this._dataStore).terminals);
      });
  }
}
